import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Heart, Calendar, MapPin, Gift, Phone, Mail,
  Menu, X, User, Bed, Image, ChevronRight, Navigation,
  Sparkles, Star, ExternalLink, Wallet, Shirt, Plane
} from 'lucide-react';
import PledgePage from './components/PledgePage';
import axios from 'axios';
import API_ENDPOINTS, { getImageUrl } from './config/api';
import { heroObjectPosition } from './utils/heroCrop';
import VenueMapModal from './components/VenueMapModal';
import PhotoGallery from './components/PhotoGallery';
import PublicModal from './components/public/PublicModal';
import RSVPForm from './components/public/RSVPForm';
import './components/WeddingTimeline.css';
import CountdownTimer from './components/public/CountdownTimer';
import FeatureCard from './components/public/FeatureCard';
import InfoCard from './components/public/InfoCard';
import TravelGuide from './components/public/TravelGuide';
import GiftReserveModal from './components/public/GiftReserveModal';
import { isModuleEnabled } from './utils/moduleVisibility';
import { GUEST_SECTION_DEFAULTS, guestSectionFromSettings } from './config/guestSection';
import { INFO_CARDS_DEFAULTS, infoCardsFromSettings, bodyParagraphs } from './config/infoCards';
import { travelGuideFromSettings } from './config/travelGuide';
import { formatScheduleTime, formatEventDayDate } from './utils/formatScheduleTime';
import SiteTypography from './components/public/SiteTypography';
import { applySiteMeta } from './utils/siteMeta';
import './App.css';
import './styles/public-cards.css';

// ─── Petal Canvas ──────────────────────────────────────────────────────────
function PetalCanvas({ isModalOpen }) {
  const canvasRef = useRef(null);
  const animationId = useRef(null);
  const petalsRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    if (!petalsRef.current) {
      petalsRef.current = Array.from({ length: 20 }, () => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        size: 4 + Math.random() * 8,
        speed: 0.3 + Math.random() * 0.7,
        drift: (Math.random() - 0.5) * 0.5,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.03,
        opacity: 0.3 + Math.random() * 0.5,
        color: ['#e8d5a8', '#d4af37', '#c9a03d', '#f5e6c8'][Math.floor(Math.random() * 4)],
      }));
    }

    const draw = () => {
      if (!isModalOpen && canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        petalsRef.current.forEach(p => {
          p.y += p.speed;
          p.x += p.drift;
          p.angle += p.spin;
          if (p.y > canvas.height + 20) {
            p.y = -20;
            p.x = Math.random() * canvas.width;
          }
          if (p.x < -20) p.x = canvas.width + 20;
          if (p.x > canvas.width + 20) p.x = -20;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.angle);
          ctx.globalAlpha = p.opacity;
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.ellipse(0, 0, p.size * 0.45, p.size, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        });
      }
      animationId.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationId.current) cancelAnimationFrame(animationId.current);
      window.removeEventListener('resize', resize);
    };
  }, [isModalOpen]);

  if (isModalOpen) return null;
  return <canvas ref={canvasRef} className="petal-canvas" />;
}

// ─── Main App ──────────────────────────────────────────────────────────────
function App() {
  const [settings, setSettings] = useState({
    wedding_title: '',
    couple_names: 'Ursula & Vitalis',
    wedding_date: '',
    footer_message: '',
    theme_primary_color: '#c9a03d',
    theme_secondary_color: '#d4af37',
    pledge_contact_info: '',
    program_timeline_title: 'Wedding\nTimeline',
    program_timeline_couple_names: '',
    program_timeline_date: '',
    program_show_timestamps: '1',
    ...GUEST_SECTION_DEFAULTS,
    ...INFO_CARDS_DEFAULTS,
  });

  const [activeModal, setActiveModal] = useState(null);
  const [showPledgePage, setShowPledgePage] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAnimOpen, setModalAnimOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [eventDays, setEventDays] = useState([]);
  const [venue, setVenue] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroImages, setHeroImages] = useState([]);
  const [slideInterval, setSlideInterval] = useState(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const handleProgramScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const clientWidth = container.clientWidth || 550;
    const index = Math.round(scrollLeft / clientWidth);
    if (index >= 0 && index < eventDays.length) {
      setActiveDayIndex(index);
    }
  };

  const [selectedGift, setSelectedGift] = useState(null);
  const [giftReserveAnim, setGiftReserveAnim] = useState(false);
  const [reservationData, setReservationData] = useState({ guest_name: '', guest_email: '', guest_phone: '' });
  const [reservationMessage, setReservationMessage] = useState('');

  const featureItems = [
    { id: 'rsvp', icon: User, title: 'RSVP', description: 'Confirm your attendance and meal preferences', featured: true },
    { id: 'program', icon: Calendar, title: 'Program', description: 'Full schedule for each day of the celebration' },
    { id: 'venue', icon: MapPin, title: 'Venue', description: 'Location, map, and directions' },
    { id: 'gifts', icon: Gift, title: 'Gift registry', description: 'Reserve a gift from our wish list' },
    { id: 'pledge', icon: Wallet, title: 'Pledge card', description: 'Cash pledge with a downloadable card' },
    { id: 'gallery', icon: Image, title: 'Gallery', description: 'Photos from our journey together' },
    { id: 'accommodation', icon: Bed, title: 'Places to stay', description: 'Recommended stays near the venue' },
    { id: 'travel_guide', icon: Plane, title: 'Travel guide', description: 'Fly, bus, or self-drive to Uganda' },
  ];

  const visibleFeatureItems = useMemo(
    () => featureItems.filter((f) => isModuleEnabled(settings, f.id)),
    [settings]
  );

  const showHero = isModuleEnabled(settings, 'hero');
  const guestSection = guestSectionFromSettings(settings);
  const infoCards = infoCardsFromSettings(settings);
  const travelGuide = travelGuideFromSettings(settings);
  const showGuestMessage =
    isModuleEnabled(settings, 'guest_message') &&
    Boolean(guestSection.title || guestSection.body);
  const showInfoCards = isModuleEnabled(settings, 'info_cards');
  const showFeaturesHeader = isModuleEnabled(settings, 'features');
  const showFeaturesSection = visibleFeatureItems.length > 0;
  const showLoveStory = isModuleEnabled(settings, 'love_story');
  const showCountdown = isModuleEnabled(settings, 'countdown');
  const showRsvp = isModuleEnabled(settings, 'rsvp');
  const showPledge = isModuleEnabled(settings, 'pledge');
  const showProgram = isModuleEnabled(settings, 'program');

  const navLinks = visibleFeatureItems.filter((f) => f.id !== 'rsvp');

  useEffect(() => {
    fetchAllData();
    const onScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', onScroll);
    setReduceMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hero slideshow - stops when modal is open
  useEffect(() => {
    if (slideInterval) clearInterval(slideInterval);
    if (heroImages.length > 1 && !isModalOpen) {
      const interval = setInterval(() => setCurrentSlide(p => (p + 1) % heroImages.length), 6000);
      setSlideInterval(interval);
    }
    return () => {
      if (slideInterval) clearInterval(slideInterval);
    };
  }, [heroImages, isModalOpen]);

  // Lock body scroll when modal, pledge page, or mobile nav is open
  useEffect(() => {
    if (isModalOpen || showPledgePage || mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isModalOpen, showPledgePage, mobileMenuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    applySiteMeta(settings);
  }, [
    settings.share_link_title,
    settings.share_link_description,
    settings.share_link_image,
    settings.wedding_title,
    settings.couple_names,
  ]);

  const fetchAllData = async () => {
    try {
      const [sRes, eRes, vRes, gRes, galRes, aRes] = await Promise.all([
        axios.get(API_ENDPOINTS.GET_SETTINGS),
        axios.get(API_ENDPOINTS.GET_EVENTS),
        axios.get(API_ENDPOINTS.GET_VENUE),
        axios.get(API_ENDPOINTS.GET_GIFTS),
        axios.get(API_ENDPOINTS.GET_GALLERY),
        axios.get(API_ENDPOINTS.GET_AIRBNB),
      ]);
      if (sRes.data.success) setSettings(p => ({ ...p, ...sRes.data.settings }));
      if (eRes.data.success) setEventDays(eRes.data.event_days);
      if (vRes.data.success) setVenue(vRes.data.venue);
      if (gRes.data.success) setGifts(gRes.data.gifts);
      if (galRes.data.success) {
        setAlbums(galRes.data.albums);
        const allPhotos = [];
        galRes.data.albums.forEach(a => a.photos?.forEach(p => allPhotos.push(p)));
        const heroMarked = allPhotos.filter(p => Number(p.show_in_hero) === 1);
        setHeroImages(heroMarked.length > 0 ? heroMarked : allPhotos);
      }
      if (aRes.data.success) setListings(aRes.data.listings);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleOpenModal = async (id) => {
    if (!isModuleEnabled(settings, id)) return;

    if (id === 'pledge') {
      try {
        const res = await axios.get(API_ENDPOINTS.GET_SETTINGS);
        if (res.data.success) setSettings((p) => ({ ...p, ...res.data.settings }));
      } catch (e) {
        console.error(e);
      }
      setShowPledgePage(true);
      setMobileMenuOpen(false);
      return;
    }

    if (id === 'program') {
      try {
        const res = await axios.get(API_ENDPOINTS.GET_EVENTS);
        if (res.data.success) setEventDays(res.data.event_days || []);
      } catch (e) {
        console.error(e);
      }
    }

    if (id === 'accommodation') {
      try {
        const res = await axios.get(API_ENDPOINTS.GET_AIRBNB);
        if (res.data.success) setListings(res.data.listings || []);
      } catch (e) {
        console.error(e);
      }
    }

    setActiveModal(id);
    setIsModalOpen(true);
    setModalAnimOpen(true);
    setMobileMenuOpen(false);
  };

  const handleCloseModal = () => {
    setModalAnimOpen(false);
  };

  const handleModalExited = () => {
    setActiveModal(null);
    setIsModalOpen(false);
  };

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };

  const handleReserveGift = async e => {
    e.preventDefault();
    try {
      const res = await axios.post(API_ENDPOINTS.RESERVE_GIFT, { ...reservationData, gift_id: selectedGift.id });
      if (res.data.success) {
        setReservationMessage('Gift reserved! Thank you!');
        setTimeout(() => { setSelectedGift(null); setReservationMessage(''); fetchAllData(); }, 2000);
      } else setReservationMessage(res.data.message);
    } catch { setReservationMessage('Error reserving gift. Please try again.'); }
  };

  const formatHeroDate = (dateStr) => {
    if (!dateStr) return 'Coming Soon';
    const iso = String(dateStr).trim().split(/[T\s]/)[0];
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return 'Coming Soon';
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTimelineDate = (dateStr) => {
    if (!dateStr) return '';
    const iso = String(dateStr).trim().split(/[T\s]/)[0];
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}.${dd}.${yy}`;
  };

  const weddingYear = settings.wedding_date
    ? new Date(settings.wedding_date).getFullYear()
    : new Date().getFullYear();
  const footerPhone = venue?.contact_phone || settings.pledge_contact_info?.split('|')[0]?.trim();
  const footerEmail = venue?.contact_email || settings.pledge_email_from;

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="cinematic-loader">
          <div className="loader-ring"></div>
          <div className="loader-ring2"></div>
          <Heart className="loader-heart" size={50} />
        </div>
        <p className="loading-text">Creating magic…</p>
      </div>
    );
  }

  return (
    <div className="app" style={{ '--primary': settings.theme_primary_color, '--secondary': settings.theme_secondary_color }}>
      <SiteTypography settings={settings} />
      {!reduceMotion && <PetalCanvas isModalOpen={isModalOpen || showPledgePage} />}

      {showPledgePage && showPledge && (
        <PledgePage settings={settings} onClose={() => setShowPledgePage(false)} />
      )}

      {/* Navbar */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo" aria-hidden="true">
            <Heart size={22} />
          </div>
          <div className="nav-links">
            {showFeaturesSection && (
              <button type="button" className="nav-link-quiet" onClick={scrollToFeatures}>
                Explore
              </button>
            )}
            {navLinks.map((f) => (
              <button key={f.id} type="button" onClick={() => handleOpenModal(f.id)}>
                {f.title}
              </button>
            ))}
            {showRsvp && (
              <button type="button" className="nav-cta" onClick={() => handleOpenModal('rsvp')}>
                RSVP
              </button>
            )}
          </div>
          <div className="nav-mobile-actions">
            {showRsvp && (
              <button
                type="button"
                className="nav-cta-mobile"
                onClick={() => handleOpenModal('rsvp')}
              >
                RSVP
              </button>
            )}
            <button
              type="button"
              className="mobile-menu-btn"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileMenuOpen((v) => !v)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="mobile-nav">
            {visibleFeatureItems.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => {
                  handleOpenModal(f.id);
                  setMobileMenuOpen(false);
                }}
              >
                {f.title}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      {showHero && (
      <section className="hero">
        <div className="hero-slideshow">
           {heroImages.length > 0 ? (
            heroImages.map((photo, i) => (
              <div
                key={photo.id ?? i}
                className={`hero-slide ${i === currentSlide ? 'active' : ''} ${loadedImages[photo.id ?? i] ? 'loaded' : ''}`}
                aria-hidden={i !== currentSlide}
              >
                <img
                  className="hero-slide-img"
                  src={getImageUrl(photo.image_url)}
                  alt=""
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  onLoad={() => setLoadedImages(prev => ({ ...prev, [photo.id ?? i]: true }))}
                  style={{ objectPosition: heroObjectPosition(photo) }}
                />
              </div>
            ))
          ) : (
            <div className="hero-slide default-bg active" />
          )}
          <div className="hero-overlay" />
        </div>

        <div className="hero-content hero-content--minimal">
          <h1 className="hero-couple-names hero-couple-names--bargiery">
            {settings.couple_names || 'Ursula & Vitalis'}
          </h1>
          <p className="hero-date-static">{formatHeroDate(settings.wedding_date)}</p>
        </div>
      </section>
      )}

      {showGuestMessage && (
        <section className="guest-message-section">
          <div className="section-container">
            {guestSection.title && (
              <p className="guest-message-title">{guestSection.title}</p>
            )}
            {guestSection.body && (
              <div className="guest-message-body">{guestSection.body}</div>
            )}
          </div>
        </section>
      )}

      {/* Features — guest planning hub */}
      {showFeaturesSection && (
      <section id="features" className="features-section">
        {showFeaturesHeader && (
        <div className="section-container">
          <div className="features-section-intro">
            <p className="guest-message-title">For our guests</p>
            <div className="features-section-body">
              <h2 className="features-section-title">Plan your visit</h2>
              <p className="features-section-text">
                Tap a card to open details.{showRsvp ? ' Start with RSVP so we can prepare for you.' : ''}
              </p>
            </div>
          </div>
        </div>
        )}
        <div className="features-grid">
          {visibleFeatureItems.map((feature, index) => (
            <FeatureCard
              key={feature.id}
              feature={feature}
              index={index}
              reduceMotion={reduceMotion}
              onClick={() => handleOpenModal(feature.id)}
            />
          ))}
        </div>
      </section>
      )}

      {showInfoCards && (
        <section className="info-cards-section" aria-label="Gifting and dress code">
          <div className="section-container">
            <div className="info-cards-grid">
              <InfoCard
                icon={Heart}
                title={infoCards.gifting.title}
                body={infoCards.gifting.body}
                footer={
                  infoCards.gifting.contact ? (
                    <a href={`tel:${infoCards.gifting.contact.replace(/[^\d+]/g, '')}`} className="info-card-contact">
                      <Phone size={18} aria-hidden="true" />
                      <span>{infoCards.gifting.contact}</span>
                    </a>
                  ) : null
                }
                index={0}
                reduceMotion={reduceMotion}
              />
              <InfoCard
                icon={Shirt}
                title={infoCards.dressCode.title}
                body={infoCards.dressCode.body}
                index={1}
                reduceMotion={reduceMotion}
              />
            </div>
          </div>
        </section>
      )}

      {/* Love Story */}
      {showLoveStory && (
      <section className="love-story">
        <div className="section-container">
          <div className="section-header">
            <span className="section-subtitle">Our journey</span>
            <h2>Love story</h2>
            <p className="section-lead section-lead--light">
              A few moments that led us here — thank you for being part of what comes next.
            </p>
            <div className="section-divider"><span>✦</span></div>
          </div>
          <div className="timeline">
            {[
              { year: String(weddingYear - 5), title: 'How we met', desc: 'A chance meeting that grew into something we never expected.', icon: Heart },
              { year: String(weddingYear - 2), title: 'The proposal', desc: 'We chose each other and began planning a life together.', icon: Sparkles },
              { year: String(weddingYear), title: 'Our wedding', desc: 'We cannot wait to celebrate this day with the people we love.', icon: Star },
            ].map((item, idx) => (
              <div key={idx} className="timeline-item">
                <div className="timeline-icon"><item.icon size={24} /></div>
                <div className="timeline-content site-card">
                  <div className="timeline-year">{item.year}</div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Countdown */}
      {showCountdown && (
      <section className="countdown">
        <div className="countdown-overlay" />
        <div className="section-container">
          <h2>Counting down</h2>
          <p className="countdown-subtitle">
            {settings.wedding_date
              ? new Date(settings.wedding_date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })
              : 'Date to be announced'}
          </p>
          <CountdownTimer weddingDate={settings.wedding_date} />
        </div>
      </section>
      )}

      {showProgram && (
      <PublicModal
        isOpen={modalAnimOpen && activeModal === 'program'}
        onClose={handleCloseModal}
        onExited={() => {
          handleModalExited();
          setActiveDayIndex(0);
        }}
        title="Wedding program"
        subtitle="Schedule for each day of the celebration"
        cleanCard={true}
      >
        <div className="program-modal">
          {eventDays.length === 0 ? (
            <p className="program-empty">The wedding program will be posted here soon. Check back closer to the date.</p>
          ) : (
            <>
              <div 
                ref={scrollContainerRef}
                onScroll={handleProgramScroll}
                className={`program-scroll-container ${eventDays.length === 1 ? 'single-day' : ''}`}
              >
                {eventDays.map((day) => {
                  const dayTitle = String(day.title || '').trim();
                  const dayDateStr = formatTimelineDate(day.event_date);
                  const heading = dayTitle || (day.day_number != 0 ? `Day ${day.day_number}` : '');
                  return (
                    <div key={day.id} className="program-day-wrapper">
                      {day.day_number != 0 && <div className="program-day-label">Day {day.day_number}</div>}
                      <div className="timeline-card">
                        {/* Header with date top-left and title top-right */}
                        <div className="timeline-header-wrapper">
                          <div className="timeline-date-left">{settings.program_timeline_date || dayDateStr}</div>
                          <div className="timeline-title-right">
                            <h1 className="timeline-card-title">
                              {(settings.program_timeline_title || 'Wedding\nTimeline').split('\n').map((line, i, arr) => (
                                <React.Fragment key={i}>
                                  {line}
                                  {i < arr.length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </h1>
                            <h2 className="timeline-card-subtitle">{settings.program_timeline_couple_names || settings.couple_names || 'Ursula & Vitalis'}</h2>
                          </div>
                        </div>
                        
                        {day.description && (
                          <p style={{ textAlign: 'center', fontFamily: 'Georgia, serif', fontSize: '0.85rem', color: '#8b7355', marginTop: '20px', marginBottom: '30px', fontStyle: 'italic' }}>
                            {day.description}
                          </p>
                        )}
                        
                        <div className="timeline-central-container">
                          <div className="timeline-central-line"></div>
                          {day.schedule?.length ? (
                            day.schedule.map((item, idx) => {
                              // Alternate between left and right
                              const isLeft = idx % 2 === 0;
                              return (
                                <div key={item.id ?? idx} className="timeline-row">
                                  {isLeft ? (
                                    <>
                                      {/* LEFT: Time and description */}
                                      <div className="timeline-left">
                                        {settings.program_show_timestamps !== '0' && (
                                          <div className="timeline-time">{formatScheduleTime(item.time)}</div>
                                        )}
                                        <div className="timeline-label">{item.activity}</div>
                                        {item.description && <div className="timeline-sublabel">{item.description}</div>}
                                      </div>
                                      
                                      {/* CENTER: Connecting line */}
                                      <div className="timeline-center">
                                        <div className="timeline-connector-line"></div>
                                        <div className="timeline-dot"></div>
                                      </div>
                                      
                                      {/* RIGHT: Empty */}
                                      <div className="timeline-right"></div>
                                    </>
                                  ) : (
                                    <>
                                      {/* LEFT: Empty */}
                                      <div className="timeline-left"></div>
                                      
                                      {/* CENTER: Connecting line */}
                                      <div className="timeline-center">
                                        <div className="timeline-dot"></div>
                                        <div className="timeline-connector-line"></div>
                                      </div>
                                      
                                      {/* RIGHT: Time and description */}
                                      <div className="timeline-right">
                                        {settings.program_show_timestamps !== '0' && (
                                          <div className="timeline-time">{formatScheduleTime(item.time)}</div>
                                        )}
                                        <div className="timeline-label">{item.activity}</div>
                                        {item.description && <div className="timeline-sublabel">{item.description}</div>}
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <p className="program-no-items">Schedule items for this day will be added soon.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {eventDays.length > 1 && (
                <div className="program-nav-dots" style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                  {eventDays.map((day, idx) => (
                    <button 
                      key={day.id} 
                      type="button"
                      className={`program-dot-indicator ${idx === activeDayIndex ? 'active' : ''}`}
                      onClick={() => {
                        const container = scrollContainerRef.current;
                        if (container) {
                          const cardWidth = container.clientWidth || 550;
                          container.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
                          setActiveDayIndex(idx);
                        }
                      }}
                      aria-label={`Go to day ${idx + 1}`}
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: idx === activeDayIndex ? 'var(--primary)' : '#ccc',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'background-color 0.3s, transform 0.2s',
                        transform: idx === activeDayIndex ? 'scale(1.2)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </PublicModal>
      )}

      <PublicModal
        isOpen={modalAnimOpen && activeModal === 'venue'}
        onClose={handleCloseModal}
        onExited={handleModalExited}
        title="Venue"
        subtitle="Where to find us and how to get there"
      >
        {venue && (
          <div className="venue-modal-modern">
            <div className="venue-header-modern">
              <div className="venue-icon-wrapper">
                <MapPin size={32} />
              </div>
              <div>
                <h3 className="venue-name">{venue.name}</h3>
                <p className="venue-address">{venue.address}</p>
              </div>
            </div>
            
            {venue.description && (
              <div className="venue-description-card">
                <p>{venue.description}</p>
              </div>
            )}
            
            {(venue.contact_phone || venue.contact_email) && (
              <div className="venue-contact-grid">
                {venue.contact_phone && (
                  <div className="contact-item">
                    <Phone size={20} />
                    <div>
                      <span className="contact-label">Phone</span>
                      <a href={`tel:${venue.contact_phone}`}>{venue.contact_phone}</a>
                    </div>
                  </div>
                )}
                {venue.contact_email && (
                  <div className="contact-item">
                    <Mail size={20} />
                    <div>
                      <span className="contact-label">Email</span>
                      <a href={`mailto:${venue.contact_email}`}>{venue.contact_email}</a>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="venue-actions">
              <button 
                onClick={() => setIsMapModalOpen(true)} 
                className="btn-primary btn-venue-action"
              >
                <MapPin size={18} /> View on Map
              </button>
              <a 
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(venue.address)}`} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-primary btn-venue-action"
              >
                <Navigation size={18} /> Get Directions
              </a>
            </div>
          </div>
        )}
      </PublicModal>

      {/* Venue Map Modal */}
      <VenueMapModal 
        isOpen={isMapModalOpen} 
        onClose={() => setIsMapModalOpen(false)} 
        venue={venue} 
      />

      <PublicModal
        isOpen={modalAnimOpen && activeModal === 'rsvp'}
        onClose={handleCloseModal}
        onExited={handleModalExited}
        title="RSVP"
        subtitle="Please respond so we can plan seating, meals, and your experience"
      >
        <RSVPForm onSuccess={() => setTimeout(handleCloseModal, 2800)} />
      </PublicModal>

      <PublicModal
        isOpen={modalAnimOpen && activeModal === 'gifts'}
        onClose={handleCloseModal}
        onExited={handleModalExited}
        title="Gift registry"
        subtitle={
          showPledge
            ? 'Reserve a gift — or make a cash pledge below'
            : 'Reserve a gift from our wish list'
        }
      >
        <div className="gifts-modal">
          <div className="gifting-intro-card site-card">
            <Heart size={32} />
            <h3>{infoCards.gifting.title}</h3>
            {bodyParagraphs(infoCards.gifting.body).map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
            {infoCards.gifting.contact && (
              <p className="gifting-intro-contact">
                <Phone size={16} aria-hidden="true" />
                <a href={`tel:${infoCards.gifting.contact.replace(/[^\d+]/g, '')}`}>
                  {infoCards.gifting.contact}
                </a>
              </p>
            )}
          </div>

          <div className="gifts-grid">
            {gifts.map(gift => (
              <div key={gift.id} className="gift-card-premium site-card">
                {gift.image_url && <img src={getImageUrl(gift.image_url)} alt={gift.name} />}
                <div className="gift-info">
                  <h4>{gift.name}</h4>
                  <p>{gift.description}</p>
                  {gift.price && <div className="gift-price">KES {gift.price}</div>}
                  <div className="gift-availability">{gift.remaining > 0 ? `${gift.remaining} of ${gift.quantity_needed} available` : 'Fully Reserved'}</div>
                  <button
                    type="button"
                    className="btn-outline-small"
                    onClick={() => {
                      setSelectedGift(gift);
                      setGiftReserveAnim(true);
                    }}
                    disabled={gift.remaining === 0}
                  >
                    Reserve Gift
                  </button>
                </div>
              </div>
            ))}
          </div>
          {showPledge && (
          <div className="cash-gift-card">
            <Heart size={24} />
            <h4>Pledge Card</h4>
            <p>Make a cash pledge and receive a downloadable pledge card with a reminder by email.</p>
            <button type="button" className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => setShowPledgePage(true)}>
              Make a Pledge
            </button>
          </div>
          )}
        </div>
        {selectedGift && (
          <GiftReserveModal
            gift={selectedGift}
            isOpen={giftReserveAnim}
            onClose={() => setGiftReserveAnim(false)}
            onExited={() => setSelectedGift(null)}
            reservationMessage={reservationMessage}
            reservationData={reservationData}
            setReservationData={setReservationData}
            onSubmit={handleReserveGift}
          />
        )}
      </PublicModal>

      <PublicModal
        isOpen={modalAnimOpen && activeModal === 'gallery'}
        onClose={handleCloseModal}
        onExited={handleModalExited}
        title="Photo gallery"
        subtitle="Browse albums from our journey"
        wide
        bodyClassName="modal-body-gallery"
      >
        <PhotoGallery albums={albums} loading={loading} />
      </PublicModal>

      <PublicModal
        isOpen={modalAnimOpen && activeModal === 'accommodation'}
        onClose={handleCloseModal}
        onExited={handleModalExited}
        title="Places to stay"
        subtitle="Recommended accommodation near the venue"
      >
        <div className="accommodation-modal-modern">
          {listings.length === 0 ? (
            <div className="accommodation-empty">
              <Bed size={48} />
              <p>Places to stay will be listed here soon.</p>
              <p className="empty-subtitle">Check back closer to the wedding date.</p>
            </div>
          ) : (
            <div className="accommodation-grid">
              {listings.map((listing) => (
                <div key={listing.id} className="accommodation-card">
                  {listing.image_url && (
                    <div className="accommodation-image">
                      <img src={getImageUrl(listing.image_url)} alt={listing.name} />
                    </div>
                  )}
                  <div className="accommodation-content">
                    <h3 className="accommodation-name">{listing.name}</h3>
                    {listing.description && <p className="accommodation-description">{listing.description}</p>}
                    
                    <div className="accommodation-details">
                      {listing.distance_km != null && listing.distance_km !== '' && (
                        <div className="accommodation-detail-item">
                          <MapPin size={16} />
                          <span>{listing.distance_km} km from venue</span>
                        </div>
                      )}
                      {listing.price_per_night && (
                        <div className="accommodation-detail-item">
                          <span className="price-badge">{listing.price_per_night}/night</span>
                        </div>
                      )}
                    </div>
                    
                    {listing.booking_url && (
                      <a 
                        href={listing.booking_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-primary btn-accommodation"
                      >
                        Book Now <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PublicModal>

      <PublicModal
        isOpen={modalAnimOpen && activeModal === 'travel_guide'}
        onClose={handleCloseModal}
        onExited={handleModalExited}
        title={travelGuide.page_title || 'Travel guide'}
        subtitle={travelGuide.page_subtitle}
        wide
      >
        <TravelGuide guide={travelGuide} reduceMotion={reduceMotion} />
      </PublicModal>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-block">
            <Heart size={20} />
            <h3>{settings.couple_names || settings.wedding_title}</h3>
            <p>{settings.footer_message || 'Thank you for being part of our celebration.'}</p>
          </div>
          <div className="footer-block">
            <h4>Questions?</h4>
            {footerPhone && (
              <p>
                <Phone size={16} /> {footerPhone}
              </p>
            )}
            {footerEmail && (
              <p>
                <Mail size={16} /> {footerEmail}
              </p>
            )}
            {!footerPhone && !footerEmail && settings.pledge_contact_info && (
              <p>{settings.pledge_contact_info}</p>
            )}
            {showRsvp && (
              <button type="button" className="footer-rsvp-link" onClick={() => handleOpenModal('rsvp')}>
                Send your RSVP <ChevronRight size={16} />
              </button>
            )}
          </div>
          {visibleFeatureItems.length > 0 && (
          <div className="footer-block">
            <h4>Quick links</h4>
            <ul className="footer-links">
              {visibleFeatureItems.map((f) => (
                <li key={f.id}>
                  <button type="button" onClick={() => handleOpenModal(f.id)}>
                    {f.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          )}
        </div>
        <div className="footer-bottom">
          <p>
            © {new Date().getFullYear()} {settings.wedding_title || settings.couple_names}. With love.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;